import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import EventIcon from '@mui/icons-material/Event';
import { JournalDatePreset, JournalEventCount, JournalFilterForm, JournalSegment } from '../../../types/ledgerJournal';
import { DATE_PRESETS } from '../journalExplorer.constants';
import { humanizeEvent } from '../journalExplorer.helpers';

interface JournalFiltersProps {
  segment: JournalSegment;
  form: JournalFilterForm;
  eventOptions: JournalEventCount[];
  loading: boolean;
  onChange: (form: JournalFilterForm) => void;
  onSearch: () => void;
  onReset: () => void;
}

type IdField = 'userId' | 'companyId' | 'employeeId';

const ID_FIELDS: Record<JournalSegment, { key: IdField; label: string }[]> = {
  B2C: [{ key: 'userId', label: 'User ID' }],
  B2B: [
    { key: 'companyId', label: 'Company ID' },
    { key: 'employeeId', label: 'Employee ID' },
    { key: 'userId', label: 'User ID' },
  ],
};

export const JournalFilters: React.FC<JournalFiltersProps> = ({
  segment,
  form,
  eventOptions,
  loading,
  onChange,
  onSearch,
  onReset,
}) => {
  const set = (patch: Partial<JournalFilterForm>) => onChange({ ...form, ...patch });

  // Digits only — the ids are integers, and a stray letter would be dropped silently on search.
  const setId = (key: IdField, value: string) => set({ [key]: value.replace(/\D/g, '') });

  const handlePreset = (_: React.MouseEvent<HTMLElement>, preset: JournalDatePreset | null) => {
    if (preset) set({ preset });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onSearch();
  };

  const selectedEvent = eventOptions.find((option) => option.event === form.event) ?? null;

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }} component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        {ID_FIELDS[segment].map(({ key, label }) => (
          <TextField
            key={key}
            size="small"
            label={label}
            value={form[key]}
            onChange={(e) => setId(key, e.target.value)}
            inputProps={{ inputMode: 'numeric' }}
            sx={{ width: 150 }}
          />
        ))}
        <Autocomplete
          size="small"
          options={eventOptions}
          value={selectedEvent}
          onChange={(_, option) => set({ event: option?.event ?? '' })}
          getOptionLabel={(option) => humanizeEvent(option.event)}
          isOptionEqualToValue={(option, value) => option.event === value.event}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.event} sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>{humanizeEvent(option.event)}</span>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                {option.count}
              </Typography>
            </Box>
          )}
          renderInput={(params) => <TextField {...params} label="Event" />}
          sx={{ width: 300 }}
        />
        <TextField
          size="small"
          label="Reference"
          placeholder="Order id, journal key…"
          value={form.reference}
          onChange={(e) => set({ reference: e.target.value })}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1, minWidth: 240 }}
        />
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mt: 2 }}>
        <EventIcon color="action" fontSize="small" />
        <ToggleButtonGroup size="small" exclusive value={form.preset} onChange={handlePreset} color="primary">
          {DATE_PRESETS.map((preset) => (
            <ToggleButton key={preset.value} value={preset.value} sx={{ textTransform: 'none', px: 1.5 }}>
              {preset.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <DatePicker
          label="From"
          value={form.from}
          onChange={(date) => set({ from: date, preset: 'custom' })}
          maxDate={form.to ?? undefined}
          format="dd MMM yyyy"
          slotProps={{ textField: { size: 'small', sx: { width: 170 } } }}
        />
        <DatePicker
          label="To"
          value={form.to}
          onChange={(date) => set({ to: date, preset: 'custom' })}
          minDate={form.from ?? undefined}
          format="dd MMM yyyy"
          slotProps={{ textField: { size: 'small', sx: { width: 170 } } }}
        />
        <Box sx={{ flex: 1 }} />
        <Button variant="text" startIcon={<RestartAltIcon />} onClick={onReset} disabled={loading}>
          Reset
        </Button>
        <Button type="submit" variant="contained" startIcon={<SearchIcon />} disabled={loading}>
          Search
        </Button>
      </Box>
    </Paper>
  );
};
